import { useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { userApi } from '@/lib/api/user';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export function ProfileSettingsPage() {
  const user = useAuthStore((s) => s.user);

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const initials = (user?.displayName ?? user?.email ?? '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isDirty = displayName.trim() !== (user?.displayName ?? '');

  const handleSave = async () => {
    if (!isDirty) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await userApi.updateMe({ displayName: displayName.trim() || null });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch {
      toast.error('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">Manage your personal account details.</p>
      </div>

      <Separator />

      {/* Avatar */}
      <div className="flex items-center gap-4">
        <Avatar size="lg">
          {user?.photoURL && <AvatarImage src={user.photoURL} />}
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium">{user?.displayName ?? 'No name set'}</p>
          <p className="text-muted-foreground text-xs">{user?.email}</p>
        </div>
      </div>

      <Separator />

      {/* Fields */}
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display-name">Display name</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              className="max-w-sm"
            />
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user?.email ?? ''} readOnly className="text-muted-foreground max-w-sm" />
            <p className="text-muted-foreground text-xs">Email cannot be changed here.</p>
          </div>

          {/* Intentionally omit internal IDs from the profile UI */}
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!isDirty || isSaving} size="sm">
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
          {saveSuccess && <span className="text-muted-foreground text-sm">Saved.</span>}
        </div>
      </div>
    </div>
  );
}
